import type { FC } from "react";
import React, { useCallback, useState } from "react";
import clsx from "clsx";
import Text from "../../../../../../shared-components/css-modules/lexus/atoms/Text";
import Button from "../../../../../../shared-components/css-modules/lexus/atoms/Button";
import type { WinterWheelType } from "../../../../../../../../common-deprecated/types/CommonTypes";
import { useCarConfigDispatch, useCarConfigSelector } from "../../../../../redux/store";
import SidePanel from "../../../../../../shared-components/css-modules/lexus/organisms/SidePanel";
import WheelDetails from "./WheelDetails";
import Price from "../../../common/price/Price";
import AddToBasket from "../../../common/AddToBasket";
import cardStyles from "../../../../../../shared-components/css-modules/lexus/molecules/Card.module.scss";
import styles from "./Wheels.module.scss";
import {
    trackWheelsTyreMoreInfoClose,
    trackWheelsTyreMoreInfoOpen,
    trackWinterWheelAddToBasket,
    trackWinterWheelRemoveFromBasket,
} from "../../../../../utils/tracking";
import WheelEcoLabel from "./WheelEcoLabel";
import { setWinterWheel } from "../../../../../redux/slices/CarConfigBasketSlice";
import { getCarDBAssetSrcSet } from "../../../../../../../shared-logic/utils/imageUtils";
import { showAccessoriesRate } from "../../../../../utils/financeInsuranceRate";
import { CarConfigLabel, useCarConfigLabel } from "../../../../../utils/labelUtils";
import type { EuLabelIcons } from "../../../../../utils/types/SectionTypes";

const WinterWheelItem: FC<{ wheel: WinterWheelType; index: number; installTheWheels: boolean; className?: string }> = ({
    wheel,
    index,
    installTheWheels,
    className,
}) => {
    const dispatch = useCarConfigDispatch();
    const [showInfo, setShowInfo] = useState<boolean>(false);

    const activeWheel = useCarConfigSelector((state) => state.basket.winterWheel);
    const commonSettings = useCarConfigSelector((state) => state.commonSettings);
    const carConfigSettings = useCarConfigSelector((state) => state.carConfigSettings);
    const sidePanelTitle = useCarConfigLabel("carConfigTyreInfo");

    const isActive = activeWheel?.id === wheel.id;

    const clickHandler = useCallback(
        (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            // Prevent double event
            event.stopPropagation();

            if (isActive) {
                trackWinterWheelRemoveFromBasket();
                dispatch(setWinterWheel(null));
            } else {
                trackWinterWheelAddToBasket();
                dispatch(setWinterWheel({ ...wheel, installTheWheels }));
            }
        },
        [dispatch, wheel, isActive, installTheWheels],
    );

    return (
        <>
            {/* Explainer: Article should have an onClick event, but also the button with the plus icon for a11y reasons.
                Add aria-pressed on true and false depending on active state and update the article classes based on that as well */}
            <article
                className={clsx(
                    cardStyles.card,
                    cardStyles.clickable,
                    styles["winter-wheel"],
                    isActive && cardStyles.active,
                    className && className,
                )}
                onClick={clickHandler}
                style={{ "--or-animation-delay": `${(index + 1) * 100 + 4}` }}
            >
                <figure className={styles["wheel-wrapper"]}>
                    <img
                        {...getCarDBAssetSrcSet(
                            commonSettings,
                            [
                                { width: 130, height: 130 },
                                { width: 260, height: 260 },
                                { width: 340, height: 340 },
                                { width: 400, height: 400 },
                                { width: 800, height: 800 },
                            ],
                            wheel.image,
                        )}
                        width="130"
                        height="130"
                        sizes="(max-width: 1199px) 46vw, 18vw"
                        alt={wheel.name}
                        loading="lazy"
                    />
                </figure>
                <Text tag="h3" variant="apheleia-body-small">
                    {wheel.name}
                </Text>
                <div className={styles.bottom}>
                    {wheel.euLabelProperties && (
                        <div className={styles.labels}>
                            {Object.entries(wheel.euLabelProperties).map(([key, value]) => (
                                <WheelEcoLabel type={key as EuLabelIcons} score={value} key={key} />
                            ))}
                        </div>
                    )}
                    <div className={styles.partinfo}>
                        {!!wheel.tyreName && (
                            <Text tag="div" variant="apheleia-body-small" color="grey-7">
                                <CarConfigLabel label="carConfigWheelTyreName" params={{ tyreName: wheel.tyreName }} />
                            </Text>
                        )}
                        {!!wheel.setPartNumber && (
                            <Text tag="div" variant="apheleia-body-small" color="grey-7">
                                <CarConfigLabel
                                    label="carConfigWheelSetPartNumber"
                                    params={{ setPartNumber: wheel.setPartNumber }}
                                />
                            </Text>
                        )}
                    </div>
                    <div className={styles.info}>
                        <div>
                            {!!wheel.tyre && (
                                <Button
                                    variant="apheleia-text"
                                    onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();

                                        trackWheelsTyreMoreInfoOpen();
                                        setShowInfo(true);
                                    }}
                                >
                                    <CarConfigLabel label="carConfigWheelMoreInfo" />
                                </Button>
                            )}
                        </div>
                        <div className={styles.price}>
                            <Text variant="apheleia-price">
                                {wheel.standard ? (
                                    <CarConfigLabel label="carConfigIncluded" />
                                ) : (
                                    <Price
                                        price={wheel.price}
                                        forceCashPrice={!showAccessoriesRate(commonSettings, carConfigSettings)}
                                    />
                                )}
                            </Text>

                            {!wheel.standard && (
                                <AddToBasket
                                    itemName={wheel.name}
                                    onClick={clickHandler}
                                    active={isActive}
                                    className={clsx(cardStyles.indicator, styles.button)}
                                    aria-pressed={isActive}
                                    data-testid="winterwheel-add-to-basket-button"
                                />
                            )}
                        </div>
                    </div>
                </div>
            </article>
            {!!wheel.tyre && (
                <SidePanel
                    open={showInfo}
                    title={sidePanelTitle}
                    onRequestClose={() => {
                        trackWheelsTyreMoreInfoClose();
                        setShowInfo(false);
                    }}
                >
                    <WheelDetails tyres={[wheel.tyre]} />
                </SidePanel>
            )}
        </>
    );
};

export default WinterWheelItem;
